/*
 * /radar-plotting — маневренный планшет.
 *
 * Смысл упражнения в том, чтобы ВИДЕТЬ треугольник скоростей, а не получать
 * четыре числа. Поэтому на планшете рисуются и засечки, и линия относительного
 * движения, и сам треугольник er–rm–em, и результат пробного манёвра — рядом,
 * в одном масштабе, чтобы разницу можно было измерить глазом.
 */

import * as E from './engine.js';
import { makeT } from '../maritime/i18n.js';

const B = window.__RP || { T: {}, cfg: {} };
const T = B.T;
const t = makeT(T);
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };

const S = {
    own: { course: 0, speed: 12 },
    plots: [
        { t: 0, bearing: 20, range: 10 },
        { t: 6, bearing: 20, range: 8 },
        { t: 12, bearing: '', range: '' },
    ],
    trial: { course: 0, speed: 12, on: false },
    result: null,
    exercise: null,
    scale: 12,
};

/* ── планшет ─────────────────────────────────────────────────────────────── */
function draw() {
    const canvas = $('rpBoard');
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    const W = canvas.clientWidth || 520, H = canvas.clientHeight || 520;
    if (canvas.width !== Math.round(W * dpr)) { canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr); }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const cx = W / 2, cy = H / 2, R = Math.min(W, H) * 0.44;
    const maxNm = S.scale;
    const px = nm => (nm / maxNm) * R;
    const P = (x, y) => [cx + px(x), cy - px(y)];

    // Кольца дальности и шкала пеленгов.
    ctx.strokeStyle = 'rgba(140,160,190,.20)';
    ctx.fillStyle = 'rgba(150,170,200,.65)';
    ctx.font = '10px ui-monospace, Menlo, monospace';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const step = maxNm <= 6 ? 1 : maxNm <= 12 ? 2 : 5;
    for (let r = step; r <= maxNm + 1e-9; r += step) {
        ctx.beginPath(); ctx.arc(cx, cy, px(r), 0, 7); ctx.stroke();
        ctx.fillText(String(r), cx + px(r) - 8, cy - 7);
    }
    ctx.strokeStyle = 'rgba(140,160,190,.14)';
    for (let a = 0; a < 360; a += 10) {
        const rad = (a - 90) * Math.PI / 180;
        const r0 = (a % 30 === 0) ? 0 : R * 0.93;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(rad) * r0, cy + Math.sin(rad) * r0);
        ctx.lineTo(cx + Math.cos(rad) * R, cy + Math.sin(rad) * R);
        ctx.stroke();
    }
    ctx.fillStyle = 'rgba(160,180,210,.8)';
    for (let a = 0; a < 360; a += 30) {
        const rad = (a - 90) * Math.PI / 180;
        ctx.fillText(String(a).padStart(3, '0'), cx + Math.cos(rad) * (R + 12), cy + Math.sin(rad) * (R + 12));
    }

    // Своё судно и свой курс.
    const oc = (S.own.course - 90) * Math.PI / 180;
    ctx.strokeStyle = 'rgba(110,197,255,.55)'; ctx.lineWidth = 1.5; ctx.setLineDash([6, 5]);
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + Math.cos(oc) * R, cy + Math.sin(oc) * R); ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.arc(cx, cy, 4.5, 0, 7); ctx.fillStyle = '#6ec5ff'; ctx.fill();

    const r = S.result;
    if (r?.ok) {
        // Линия относительного движения от первой засечки за точку CPA.
        const p0 = E.toXY(r.plots[0].bearing, r.plots[0].range);
        const far = { x: r.cpaPoint.x + r.relative.vx * 0.6, y: r.cpaPoint.y + r.relative.vy * 0.6 };
        ctx.strokeStyle = 'rgba(245,158,11,.85)'; ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(...P(p0.x, p0.y)); ctx.lineTo(...P(far.x, far.y));
        ctx.stroke();

        // Точка CPA и отрезок до неё.
        ctx.strokeStyle = 'rgba(239,68,68,.8)'; ctx.setLineDash([4, 4]); ctx.lineWidth = 1.4;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(...P(r.cpaPoint.x, r.cpaPoint.y)); ctx.stroke();
        ctx.setLineDash([]);
        ctx.beginPath(); ctx.arc(...P(r.cpaPoint.x, r.cpaPoint.y), 4, 0, 7);
        ctx.fillStyle = '#ef4444'; ctx.fill();

        // Засечки.
        r.plots.forEach((p, i) => {
            const q = E.toXY(p.bearing, p.range);
            ctx.beginPath(); ctx.arc(...P(q.x, q.y), 5, 0, 7);
            ctx.fillStyle = i === r.plots.length - 1 ? '#22c55e' : 'rgba(34,197,94,.55)';
            ctx.fill();
            ctx.strokeStyle = 'rgba(0,0,0,.4)'; ctx.lineWidth = 1; ctx.stroke();
            ctx.fillStyle = 'rgba(200,220,240,.9)';
            ctx.fillText(String(p.t), ...P(q.x, q.y).map((v, k) => k ? v - 12 : v));
        });

        // Пробный манёвр — поверх, другим цветом.
        if (S.trial.on) {
            const tr = E.trial(r, S.trial);
            if (tr) {
                const last = E.toXY(r.bearing, r.range);
                const end = { x: last.x + Math.sin(tr.drm * Math.PI / 180) * maxNm * 1.4,
                              y: last.y + Math.cos(tr.drm * Math.PI / 180) * maxNm * 1.4 };
                ctx.strokeStyle = 'rgba(99,102,241,.9)'; ctx.lineWidth = 2; ctx.setLineDash([7, 4]);
                ctx.beginPath(); ctx.moveTo(...P(last.x, last.y)); ctx.lineTo(...P(end.x, end.y)); ctx.stroke();
                ctx.setLineDash([]);
            }
        }
    }
    drawTriangle();
}

/** Треугольник скоростей отдельно — в узлах, а не в милях. */
function drawTriangle() {
    const canvas = $('rpTriangle');
    if (!canvas) return;
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    const W = canvas.clientWidth || 260, H = canvas.clientHeight || 260;
    if (canvas.width !== Math.round(W * dpr)) { canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr); }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const r = S.result;
    if (!r?.ok) return;

    const own = E.toXY(S.own.course, S.own.speed);
    const rel = { x: r.relative.vx, y: r.relative.vy };
    const tgt = E.toXY(r.target.course, r.target.speed);
    const maxV = Math.max(Math.hypot(own.x, own.y), Math.hypot(tgt.x, tgt.y), Math.hypot(rel.x, rel.y), 1) * 1.25;
    const cx = W * 0.5, cy = H * 0.62, R = Math.min(W, H) * 0.42;
    const P = (x, y) => [cx + (x / maxV) * R, cy - (y / maxV) * R];

    const arrow = (from, to, colour, label) => {
        const [x1, y1] = P(from.x, from.y), [x2, y2] = P(to.x, to.y);
        ctx.strokeStyle = colour; ctx.lineWidth = 2.4;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        const a = Math.atan2(y2 - y1, x2 - x1);
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - 9 * Math.cos(a - 0.4), y2 - 9 * Math.sin(a - 0.4));
        ctx.lineTo(x2 - 9 * Math.cos(a + 0.4), y2 - 9 * Math.sin(a + 0.4));
        ctx.closePath(); ctx.fillStyle = colour; ctx.fill();
        if (label) {
            ctx.fillStyle = colour;
            ctx.font = '600 12px system-ui, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(label, (x1 + x2) / 2 + 12, (y1 + y2) / 2 - 6);
        }
    };
    const O = { x: 0, y: 0 };
    arrow(O, own, '#6ec5ff', 'er');
    arrow(own, tgt, '#f59e0b', 'rm');
    arrow(O, tgt, '#22c55e', 'em');
}

/* ── ввод и расчёт ───────────────────────────────────────────────────────── */
function readInputs() {
    S.own.course = Number($('rpOwnCourse').value) || 0;
    S.own.speed = Number($('rpOwnSpeed').value) || 0;
    const rows = $('rpPlots').querySelectorAll('.rp-plot');
    S.plots = [...rows].map(row => ({
        t: Number(row.querySelector('.p-t').value),
        bearing: Number(row.querySelector('.p-b').value),
        range: Number(row.querySelector('.p-r').value),
    })).filter(p => Number.isFinite(p.t) && Number.isFinite(p.bearing) && Number.isFinite(p.range) && p.range > 0);
}

function recalc() {
    readInputs();
    const r = S.result = E.solve(S.plots, S.own);
    if (S.result.ok) S.result.ownCourse = S.own.course;
    const box = $('rpOut');
    box.textContent = '';

    if (!r.ok) {
        box.append(el('p', 'rp-bad', t('err_' + r.error)));
        S.scale = 12;
        draw();
        return;
    }
    S.scale = Math.max(4, Math.ceil(Math.max(...S.plots.map(p => p.range)) * 1.15));

    const facts = el('div', 'tc-stats');
    const add = (l, v, sub, state) => {
        const f = el('div', 'tc-stat' + (state ? ' ' + state : ''));
        f.append(el('div', 'tc-stat-l', l), el('div', 'tc-stat-v', v));
        if (sub) f.append(el('div', 'tc-stat-s', sub));
        facts.append(f);
    };
    const danger = r.cpa < 1 ? 'bad' : r.cpa < 2 ? 'warn' : 'good';
    add(t('f_cpa'), E.fmtNm(r.cpa) + ' ± ' + E.fmtNm(r.confidence.sigmaCpa),
        t('f_cpa_sub', { brg: E.fmtBrg(r.cpaBearing) }), r.closing ? danger : '');
    add(t('f_tcpa'), r.closing ? E.fmtMin(r.tcpaMin) : t('f_tcpa_past'),
        r.closing ? t('f_tcpa_sub') : t('f_tcpa_past_sub'), r.closing && r.tcpaMin < 20 ? 'warn' : '');
    add(t('f_tcourse'), E.fmtBrg(r.target.course), t('f_tcourse_sub', { n: r.confidence.sigmaCourse.toFixed(1) }));
    add(t('f_tspeed'), r.target.speed.toFixed(1) + ' ' + t('unit_kn'),
        t('f_tspeed_sub', { n: r.confidence.sigmaSpeed.toFixed(1) }));
    add(t('f_drm'), E.fmtBrg(r.relative.drm), t('f_drm_sub', { n: r.relative.speed.toFixed(1) }));
    add(t('f_aspect'), Math.abs(r.target.aspect).toFixed(0) + '° ' + t('side_' + r.target.side), t('f_aspect_sub'));
    box.append(facts);

    if (r.steadyBearing) box.append(el('p', 'rp-danger', t('steady_bearing')));
    if (r.confidence.weak) box.append(el('p', 'rp-warn', t('weak_solution', {
        cpa: E.fmtNm(r.confidence.sigmaCpa), min: r.confidence.intervalMin,
    })));
    if (r.bow) box.append(el('p', 'tc-note', t('bow_crossing', {
        nm: E.fmtNm(r.bow.rangeNm), min: Math.round(r.bow.minutes),
    })));

    renderTrial();
    draw();
}

function renderTrial() {
    const box = $('rpTrialOut');
    box.textContent = '';
    if (!S.result?.ok || !S.trial.on) return;
    const tr = E.trial(S.result, S.trial);
    if (!tr) return;
    const before = S.result.cpa;
    const row = el('div', 'rp-trial');
    row.append(el('div', 'rp-trial-h', t('trial_result', {
        course: E.fmtBrg(S.trial.course), speed: S.trial.speed,
    })));
    const delta = tr.cpa - before;
    const d = el('div', 'rp-trial-v');
    d.append(el('span', 'was', E.fmtNm(before)));
    d.append(el('span', 'arrow', ' → '));
    d.append(el('span', delta >= 0 ? 'up' : 'down', E.fmtNm(tr.cpa) + ' ' + t('unit_nm')));
    row.append(d);
    row.append(el('div', 'tc-stat-s', tr.closing ? t('trial_tcpa', { n: E.fmtMin(tr.tcpaMin) }) : t('trial_opening')));
    box.append(row);

    const sols = E.courseForCpa(S.result, S.own.speed, Number($('rpWantCpa').value) || 2);
    if (sols.length) {
        const s = el('div', 'tc-note');
        s.textContent = t('courses_for', {
            want: Number($('rpWantCpa').value) || 2,
            list: sols.map(x => E.fmtBrg(x.course)).join(' ' + t('or') + ' '),
        });
        box.append(s);
    }
}

/* ── строки засечек ──────────────────────────────────────────────────────── */
function renderPlots() {
    const box = $('rpPlots');
    box.textContent = '';
    S.plots.forEach((p, i) => {
        const row = el('div', 'rp-plot');
        const mk = (cls, val, ph, step) => {
            const inp = el('input', 'rp-cell ' + cls);
            inp.type = 'number'; inp.value = val === '' ? '' : val;
            inp.placeholder = ph; inp.step = step;
            inp.addEventListener('input', () => { clearTimeout(renderPlots._d); renderPlots._d = setTimeout(recalc, 200); });
            return inp;
        };
        row.append(el('span', 'rp-plot-i', String(i + 1)));
        row.append(mk('p-t', p.t, t('ph_time'), '1'));
        row.append(mk('p-b', p.bearing, t('ph_bearing'), '0.1'));
        row.append(mk('p-r', p.range, t('ph_range'), '0.1'));
        box.append(row);
    });
}

/* ── упражнение ──────────────────────────────────────────────────────────── */
function newExercise() {
    const ex = E.makeExercise(Math.random() * 2 ** 32, Number($('rpLevel').value) || 1);
    S.exercise = ex;
    S.own = { ...ex.own };
    $('rpOwnCourse').value = ex.own.course;
    $('rpOwnSpeed').value = ex.own.speed;
    S.plots = ex.plots.map(p => ({ ...p }));
    renderPlots();
    $('rpExAnswer').hidden = true;
    $('rpExCheck').hidden = false;
    $('rpExGuessC').value = '';
    $('rpExGuessS').value = '';
    recalc();
}

function checkExercise() {
    if (!S.exercise) return;
    const gc = Number($('rpExGuessC').value), gs = Number($('rpExGuessS').value);
    const a = S.exercise.answer;
    const dc = Math.abs(((gc - a.course + 540) % 360) - 180);
    const ds = Math.abs(gs - a.speed);
    const box = $('rpExAnswer');
    box.hidden = false;
    box.textContent = '';
    const good = dc <= 5 && ds <= 1;
    box.className = 'rp-exans ' + (good ? 'ok' : 'no');
    box.append(el('div', 'rp-trial-h', good ? t('ex_right') : t('ex_wrong')));
    box.append(el('div', null, t('ex_answer', { course: E.fmtBrg(a.course), speed: a.speed })));
    box.append(el('div', 'tc-stat-s', t('ex_yours', { dc: dc.toFixed(1), ds: ds.toFixed(1) })));
    window.__aeFunnel?.('exercise', { correct: good });
}

/* ── подключение ─────────────────────────────────────────────────────────── */
function bind() {
    for (const id of ['rpOwnCourse', 'rpOwnSpeed']) {
        $(id).addEventListener('input', () => { clearTimeout(bind._d); bind._d = setTimeout(recalc, 180); });
    }
    $('rpAddPlot').addEventListener('click', () => {
        const last = S.plots[S.plots.length - 1];
        S.plots.push({ t: (Number(last?.t) || 0) + 6, bearing: '', range: '' });
        renderPlots();
    });
    $('rpTrialOn').addEventListener('change', e => {
        S.trial.on = e.target.checked;
        $('rpTrialBox').hidden = !S.trial.on;
        renderTrial(); draw();
    });
    for (const [id, key] of [['rpTrialCourse', 'course'], ['rpTrialSpeed', 'speed']]) {
        $(id).addEventListener('input', e => { S.trial[key] = Number(e.target.value) || 0; renderTrial(); draw(); });
    }
    $('rpWantCpa').addEventListener('input', renderTrial);
    $('rpExNew').addEventListener('click', newExercise);
    $('rpExCheck').addEventListener('click', checkExercise);
    let rt;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(draw, 140); });
}

function boot() {
    try {
        bind();
        renderPlots();
        $('rpTrialCourse').value = S.own.course;
        $('rpTrialSpeed').value = S.own.speed;
        recalc();
        window.__stage?.('ready');
    } catch (err) {
        window.__handleError?.(err, 'radar-plotting');
        const b = $('rpOut');
        if (b) b.textContent = t('load_error');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
